import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import {
  chargeFieldConfiguration,
  chargeInitialValues,
} from "../../../formConfigs/Fleet/chargeFieldConfig";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { fetchAllDrivers } from "../IncidentPopup/IncidentPopup";
import { getId } from "../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";

const ChargePopup: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [driverData, setDriverData] = useState<OptionType[]>([]);
  const [ownerCompanies, setOwnerCompanies] = useState<OptionType[]>([]);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { token } = useAuth();

  const chargeMutation = useMutation({
    mutationFn: (data: any) =>
      axios.post(`${apiVariable}/charges`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    onSuccess: () => {
      setStatus("success");
      handleClose();
      queryClient.invalidateQueries({ queryKey: ["charges"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to submit charge");
    },
  });

  const { data: allDrivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchAllDrivers(token),
    enabled: !!token,
  });

  const { data: companiesData } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: !!token,
  });

  useEffect(() => {
    if (allDrivers) {
      setDriverData(allDrivers);
    }
  }, [allDrivers]);

  useEffect(() => {
    if (companiesData) {
      setOwnerCompanies(companiesData);
    }
  }, [companiesData]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setStatus("idle");

  const submitHandler = (data: any) => {
    const requiredFields = ["reason", "caseNumber", "dateCommited", "amount"];
    const missingFields = requiredFields.filter((field) => !data[field]);
    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    if (!data.driverId && !data.ownerCompanyId) {
      setStatus("error");
      setErrorMessage("Please select a driver or a company.");
      return;
    }

    data.driverId = getId(data.driverId) || null;
    data.ownerCompanyId = getId(data.ownerCompanyId) || null;

    chargeMutation.mutate(data);
  };

  return (
    <>
      <Button variant="contained" color="primary" onClick={handleOpen}>
        Create Charge
      </Button>
      <GenericFormPopup
        formFields={chargeFieldConfiguration}
        initialValues={chargeInitialValues}
        title="Create Charge"
        onSubmit={submitHandler}
        open={open}
        onClose={handleClose}
        autocompleteData={{
          drivers: driverData,
          ownerCompanies: ownerCompanies,
        }}
        loading={chargeMutation.isPending}
      />
      <StatusSnackbar
        status={status}
        successMessage="Charge added successfully"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default ChargePopup;
